import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { findRouteName } from '@app/routing/business-routes';
import { RouterService } from '@app/routing/router.service';
import { ReturnService } from '@app/shared/services/return.service';
import { isNotBlank } from '@app/utils/string';
import { StringMap } from '@app/utils/string-map';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.scss'],
  host: {
    class: 'portal-link',
  },
  standalone: false,
})
export class LinkComponent implements OnChanges, OnDestroy, OnInit {
  @Input()
  activeRoutes?: string[];

  @Input()
  classes?: string;

  @Input()
  disabled: boolean = false;

  @Input()
  params: StringMap<number | string>;

  @Input()
  returnTo?: string;

  @Input()
  returnToLabel?: string;

  @Input()
  returnToParams?: StringMap<number | string>;

  @Input()
  route: string;

  @Input()
  routerLinkActive: string = '';

  @Input()
  routerLinkActiveExact: boolean = true;

  @Input()
  value: string = undefined;

  @ViewChild('link')
  link: ElementRef<HTMLLinkElement>;

  classNames$: Observable<string[]>;
  href$!: Observable<any[]>;

  private activeRoutes$ = new BehaviorSubject<string[]>([]);
  private classes$ = new BehaviorSubject<string>('');
  private params$ = new BehaviorSubject<StringMap<number | string>>({});
  private routerLinkActive$ = new BehaviorSubject<string>('');
  private route$ = new ReplaySubject<string>(1);

  constructor(
    private returnService: ReturnService,
    private routerService: RouterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('params' in changes) {
      this.params$.next(changes.params.currentValue);
    }

    if ('route' in changes) {
      this.route$.next(changes.route.currentValue);
    }

    if ('activeRoutes' in changes) {
      this.activeRoutes$.next(changes.activeRoutes.currentValue ?? []);
    }

    if ('classes' in changes) {
      this.classes$.next(changes.classes.currentValue ?? '');
    }

    if ('routerLinkActive' in changes) {
      this.routerLinkActive$.next(changes.routerLinkActive.currentValue ?? '');
    }

    if ('disabled' in changes && changes.disabled.firstChange === false) {
      if (changes.disabled.currentValue === true) {
        this.disable();
      } else {
        this.initialize();
      }
    }
  }

  ngOnDestroy(): void {
    this.classes$.complete();
    this.routerLinkActive$.complete();
    this.activeRoutes$.complete();
    this.params$.complete();
    this.route$.complete();
  }

  ngOnInit(): void {
    this.initialize();
  }

  updateReturnToAddress(): void {
    if (this.returnTo != null) {
      this.returnService.updateReturnAddress({
        label: this.returnToLabel,
        params: this.returnToParams,
        route: this.returnTo,
      });
    }
  }

  private disable(): void {
    this.classNames$ = null;
    this.href$ = null;
  }

  private initialize(): void {
    // No need to initialize again
    if (this.href$ != null && this.classNames$ != null) {
      return;
    }

    this.href$ = combineLatest([this.params$, this.route$]).pipe(
      mergeMap(([params, route]) => this.routerService.resolveLink$(route, params)),
      map((route) => (route.nonUrlParams != null ? [route.url, route.nonUrlParams] : [route.url])),
      untilDestroyed(this)
    );

    this.classNames$ = combineLatest([
      this.routerService.routeDataChange$,
      this.classes$,
      this.activeRoutes$,
      this.routerLinkActive$,
    ]).pipe(
      map(([{ current }, classes, activeRoutes, routerLinkActive]) => {
        const classList = [classes];

        if (isNotBlank(routerLinkActive) && activeRoutes != null) {
          const currentRoute = findRouteName(current.url);

          if (activeRoutes.includes(currentRoute)) {
            classList.push(routerLinkActive);
          }
        }

        return classList;
      }),
      untilDestroyed(this)
    );
  }
}
